import { Injectable } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

/**
 * MarkdownService
 * 
 * Converts markdown strings to safe HTML for display
 * Uses a regex-based approach (simple but effective)
 * 
 * Future Enhancement: Replace with 'marked' library for advanced markdown support
 * Install: npm install marked
 * Then: import { marked } from 'marked';
 */
@Injectable({
    providedIn: 'root'
})
export class MarkdownService {
    // Cache to avoid re-processing same markdown strings
    private cache = new Map<string, SafeHtml>();
    
    // Configuration for markdown parsing
    private config = {
        enableCache: true,
        maxCacheSize: 100
    };

    constructor(private sanitizer: DomSanitizer) {}

    /**
     * Convert markdown string to safe HTML
     * 
     * @param markdown Raw markdown string from backend
     * @returns SafeHtml object that can be safely used in templates
     * 
     * Example:
     * ```
     * const html = this.markdownService.toHtml("# Header\n**bold**");
     * // Returns: <h1>Header</h1><p><strong>bold</strong></p>
     * ```
     */
    public toHtml(markdown: string): SafeHtml {
        if (!markdown || markdown.trim().length === 0) {
            return this.sanitizer.bypassSecurityTrustHtml('');
        }

        const normalizedMarkdown = this.normalizeMarkdownInput(markdown);

        // Check cache first
        if (this.config.enableCache && this.cache.has(normalizedMarkdown)) {
            return this.cache.get(normalizedMarkdown)!;
        }

        // Convert markdown to HTML
        let html = this.convertMarkdownToHtml(normalizedMarkdown);

        // Sanitize to prevent XSS
        const safeHtml = this.sanitizer.bypassSecurityTrustHtml(html);

        // Store in cache
        if (this.config.enableCache) {
            this.cacheHtml(normalizedMarkdown, safeHtml);
        }

        return safeHtml;
    }

    /**
     * Normalize markdown payload to ensure consistent rendering across all sources.
     * Handles both actual new lines and escaped sequences from API/WebSocket payloads.
     */
    private normalizeMarkdownInput(markdown: string): string {
        let normalized = markdown.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
        normalized = this.decodeWrappedMarkdownString(normalized);
        normalized = normalized.replace(/\\n/g, '\n');
        normalized = normalized.replace(/\\t/g, '    ');
        normalized = this.decodeWrappedMarkdownString(normalized);
        return normalized;
    }

    /**
     * Some backend payloads arrive as a quoted JSON string containing escaped markdown.
     * Example: "## Title\\n- item"
     * This helper unwraps/decode such payloads while leaving normal markdown untouched.
     */
    private decodeWrappedMarkdownString(value: string): string {
        const trimmed = value.trim();

        if (trimmed.length < 2) {
            return value;
        }

        const hasDoubleQuotes = trimmed.startsWith('"') && trimmed.endsWith('"');
        const hasSingleQuotes = trimmed.startsWith("'") && trimmed.endsWith("'");

        if (!hasDoubleQuotes && !hasSingleQuotes) {
            return value;
        }

        if (hasDoubleQuotes) {
            try {
                const parsed = JSON.parse(trimmed);
                if (typeof parsed === 'string' && parsed.trim().length > 0) {
                    return parsed;
                }
            } catch {
                // Fallback to manual unescape below
            }
        }

        const unquoted = trimmed.slice(1, -1)
            .replace(/\\"/g, '"')
            .replace(/\\'/g, "'")
            .replace(/\\\\/g, '\\');

        const looksLikeMarkdown = /(^|\n)\s*#{1,6}\s+|(^|\n)\s*[-*]\s+|(^|\n)\s*\d+\.\s+|\[[^\]]+\]\([^)]+\)|\[Cite:/m.test(unquoted);

        return looksLikeMarkdown ? unquoted : value;
    }

    /**
     * Clear the markdown cache
     * Call this when user navigates away or resets chat
     */
    public clearCache(): void {
        this.cache.clear();
    }

    /**
     * Get cache statistics (for debugging)
     */
    public getCacheStats(): { size: number; maxSize: number } {
        return {
            size: this.cache.size,
            maxSize: this.config.maxCacheSize
        };
    }

    /**
     * Private method: Convert markdown to HTML using regex patterns
     * 
     * Handles:
     * - Headers (# ## ###)
     * - Bold text (**text**)
     * - Italic text (*text*)
     * - Links [text](url)
    * - Tables (| col | col |)
     * - Bullet lists (- item)
     * - Numbered lists (1. 2. 3.)
     * - Code blocks (```code```)
     * - LaTeX formulas (\[ \])
     * - Horizontal rules (---)
     */
    private convertMarkdownToHtml(markdown: string): string {
        let html = markdown;

        // Process in specific order to avoid conflicts

        // 1. Code blocks (``` ```) - Process first to protect content
        html = this.processCodeBlocks(html);

        // 2. LaTeX formulas (\[ \]) - Process early to protect from other replacements
        html = this.processLatexFormulas(html);

        // 3. Inline code (`code`) - Single backticks
        html = html.replace(/`([^`]+)`/g, '<code class="inline-code">$1</code>');

        // 3.1 Normalize auto-appended citation headings so they don't overpower
        // short answer text blocks.
        html = html.replace(
            /^###\s*Sources Referenced\s*:?\s*$/gim,
            '<h4 class="sources-referenced-heading">Sources Referenced</h4>'
        );
        html = html.replace(
            /^##\s*Sources Referenced\s*:?\s*$/gim,
            '<h4 class="sources-referenced-heading">Sources Referenced</h4>'
        );

        // 4. Headers (# to ######)
        html = html.replace(/^( {0,3})(#{1,6})[ \t]+(.+?)\s*#*\s*$/gm, (match, _leading, hashes, content) => {
            const level = String(hashes).length;
            return `<h${level}>${String(content).trim()}</h${level}>`;
        });

        // 5. Horizontal rules (---)
        html = html.replace(/^\-{3,}$/gm, '<hr>');

        // 6. Links ([text](url)) - Process before bold/italic
        html = html.replace(/\[([^\]]+)\]\(([^\)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');

        // 6.1 Plain URLs (https://... or www....)
        html = html.replace(
            /(^|[\s(>])((?:https?:\/\/|www\.)[^\s<)]+)(?=($|[\s),.;!?]))/g,
            (match, prefix, url) => {
                const href = String(url).startsWith('www.') ? `https://${url}` : url;
                return `${prefix}<a href="${href}" target="_blank" rel="noopener noreferrer">${url}</a>`;
            }
        );

        // 7. Bold (**text**) - Improved regex to handle text followed by punctuation
        html = html.replace(/\*\*([^\*]+)\*\*/g, '<strong>$1</strong>');

        // 8. Italic (*text*) - Be careful not to match bold
        html = html.replace(/\*([^\*]+)\*/g, '<em>$1</em>');

        // 9. Tables (| col | col |)
        html = this.processMarkdownTables(html);

        // 10. Bullet lists (- item)
        html = this.processBulletLists(html);

        // 11. Numbered lists (1. item)
        html = this.processNumberedLists(html);

        // 12. Line breaks and paragraphs
        html = this.processParagraphs(html);

        // 13. Ensure raw HTML tables also get table-level horizontal scrolling
        html = this.wrapHtmlTables(html);

        return html;
    }

    private wrapHtmlTables(html: string): string {
        const tableRegex = /<table\b[\s\S]*?<\/table>/gi;
        const wrapperStartRegex = /<div\s+class=["']markdown-table-wrapper["'][^>]*>\s*$/i;

        return html.replace(tableRegex, (tableHtml, offset, fullHtml) => {
            const table = new DOMParser().parseFromString(tableHtml, 'text/html').querySelector('table');
            const rows = Array.from(table?.rows || []);
            const headers = Array.from(rows[0]?.cells || [], cell => (cell.textContent || '').trim());
            const isUngroupedComparison = headers.length >= 3
                && /^attributes$/i.test(headers[0])
                && /^legacy pipe spec\b/i.test(headers[1])
                && headers.slice(2).every(header => /^CVX\s*\/\s*(?:CES|ESRA)(?:\s*\/\s*(?:CES|ESRA))*\s+equivalents?\b/i.test(header))
                && !table?.classList.contains('legacy-spec-comparison')
                && rows.every(row => row.cells.length === headers.length
                    && Array.from(row.cells).every(cell => cell.rowSpan === 1 && cell.colSpan === 1));
            if (isUngroupedComparison) {
                const markdownRows = rows.map(row => `| ${Array.from(row.cells, cell => cell.innerHTML
                    .replace(/\|/g, '\\|').replace(/\r?\n/g, ' ')).join(' | ')} |`);
                markdownRows.splice(1, 0, `| ${headers.map(() => '---').join(' | ')} |`);
                const formatted = this.processMarkdownTables(markdownRows.join('\n'));
                tableHtml = new DOMParser().parseFromString(formatted, 'text/html').querySelector('table')!.outerHTML;
            }
            const lookBehind = String(fullHtml).slice(Math.max(0, Number(offset) - 160), Number(offset));
            if (wrapperStartRegex.test(lookBehind)) {
                return tableHtml;
            }

            return `<div class="markdown-table-wrapper" style="display:block;max-width:100%;overflow-x:auto;overflow-y:hidden;">${tableHtml}</div>`;
        });
    }

    /**
     * Process GitHub-style markdown tables.
     *
     * Example:
     * | Name | Value |
     * |------|-------|
     * | A    | 123   |
     */
    private processMarkdownTables(html: string): string {
        const lines = html.split('\n');
        const output: string[] = [];

        const isTableSeparator = (line: string): boolean => {
            const trimmed = line.trim();
            return /^\|?[\s:-]+(\|[\s:-]+)+\|?$/.test(trimmed);
        };

        const splitTableRow = (line: string): string[] => {
            let row = line.trim();
            // Replace escaped pipes \| with a placeholder before splitting on |
            const ESCAPED_PIPE_PLACEHOLDER = '\x00PIPE\x00';
            row = row.replace(/\\\|/g, ESCAPED_PIPE_PLACEHOLDER);
            if (row.startsWith('|')) {
                row = row.substring(1);
            }
            if (row.endsWith('|')) {
                row = row.substring(0, row.length - 1);
            }
            // Split on real column separators, then restore escaped pipes as literal |
            return row.split('|').map(cell => cell.trim().replace(new RegExp(ESCAPED_PIPE_PLACEHOLDER, 'g'), '|'));
        };

        let index = 0;
        while (index < lines.length) {
            const headerLine = lines[index];
            const separatorLine = index + 1 < lines.length ? lines[index + 1] : '';

            const isPotentialTableHeader = headerLine.includes('|') && !headerLine.trim().startsWith('<');
            if (!isPotentialTableHeader || !isTableSeparator(separatorLine)) {
                output.push(headerLine);
                index++;
                continue;
            }

            const headerCells = splitTableRow(headerLine);
            if (headerCells.length < 2) {
                output.push(headerLine);
                index++;
                continue;
            }

            const rowLines: string[] = [];
            let rowIndex = index + 2;
            while (rowIndex < lines.length) {
                const rowLine = lines[rowIndex];
                const trimmed = rowLine.trim();

                if (!trimmed || !rowLine.includes('|') || isTableSeparator(rowLine) || trimmed.startsWith('<')) {
                    break;
                }

                rowLines.push(rowLine);
                rowIndex++;
            }

            const isSpecComparison = headerCells.length >= 3
                && /^attributes$/i.test(this.htmlToText(headerCells[0]))
                && /^legacy pipe spec\b/i.test(this.htmlToText(headerCells[1]))
                && headerCells.slice(2).every(cell => /^CVX\s*\/\s*(?:CES|ESRA)(?:\s*\/\s*(?:CES|ESRA))*\s+equivalents?\b/i.test(this.htmlToText(cell)));
            let thead = `<thead><tr>${headerCells.map(cell => `<th>${cell}</th>`).join('')}</tr></thead>`;
            const tbodyRows = rowLines.map(line => {
                const cells = splitTableRow(line);
                return `<tr>${cells.map(cell => {
                    const content = isSpecComparison
                        ? cell.replace(/(?:<br\s*\/?>\s*)+(?=<(?:sup\b[^>]*>\s*(?:<a\b[^>]*>\s*)?|a\b[^>]*>\s*(?:<sup\b[^>]*>\s*)?)\[\d+\])/gi, ' ')
                        : cell;
                    return `<td>${content}</td>`;
                }).join('')}</tr>`;
            }).join('');
            const tbody = `<tbody>${tbodyRows}</tbody>`;

            const tableClass = isSpecComparison ? ' class="legacy-spec-comparison"' : '';
            if (isSpecComparison && headerCells.slice(1).every(cell => cell.includes(':') && cell.slice(cell.indexOf(':') + 1).trim())) {
                const specHeaders = headerCells.slice(1).map(cell => cell.slice(cell.indexOf(':') + 1).trim());
                const equivalentTypes = ['CES', 'ESRA'].filter(type => headerCells.slice(2).some(cell =>
                    new RegExp(`\\b${type}\\b`, 'i').test(cell.split(':')[0])));
                const equivalentHeading = `CVX/${equivalentTypes.join('/')} Equivalents`;
                thead = `<thead><tr><th rowspan="2" scope="col">${headerCells[0]}</th><th scope="colgroup">Legacy Pipe Spec</th><th colspan="${specHeaders.length - 1}" scope="colgroup">${equivalentHeading}</th></tr><tr>${specHeaders.map(cell => `<th scope="col">${cell}</th>`).join('')}</tr></thead>`;
            }

            const columns = isSpecComparison
                ? `<colgroup><col class="comparison-attributes">${headerCells.slice(1).map(() => '<col class="comparison-spec">').join('')}</colgroup>`
                : '';
            const tableStyle = isSpecComparison
                ? `--spec-column-count:${headerCells.length - 1};`
                : 'width:max-content;min-width:100%;border-collapse:collapse;';
            output.push(`<div class="markdown-table-wrapper" style="display:block;max-width:100%;overflow-x:auto;overflow-y:hidden;"><table${tableClass} style="${tableStyle}">${columns}${thead}${tbody}</table></div>`);
            index = rowIndex;
        }

        return output.join('\n');
    }

    /**
     * Process code blocks (```code```)
     */
    private processCodeBlocks(markdown: string): string {
        const codeBlockRegex = /```([\s\S]*?)```/g;
        let replacement = 0;
        const codeBlocks: string[] = [];

        // Store code blocks and replace with placeholders
        const result = markdown.replace(codeBlockRegex, (match, code) => {
            codeBlocks.push(code.trim());
            return `__CODE_BLOCK_${replacement++}__`;
        });

        // Now process the rest of markdown, then restore code blocks
        return result.split('\n').map(line => {
            for (let i = 0; i < codeBlocks.length; i++) {
                if (line.includes(`__CODE_BLOCK_${i}__`)) {
                    return `<pre><code>${this.escapeHtml(codeBlocks[i])}</code></pre>`;
                }
            }
            return line;
        }).join('\n');
    }

    /**
     * Process LaTeX formulas (\[ formula \])
     * Converts LaTeX display math to MathJax-compatible format
     */
    private processLatexFormulas(markdown: string): string {
        // Handle display math: \[ ... \]
        markdown = markdown.replace(/\\\[([\s\S]*?)\\\]/g, (match, formula) => {
            // Escape for HTML but preserve the formula
            const escaped = formula.trim();
            return `<div class="latex-formula">$$${escaped}$$</div>`;
        });

        // Handle inline math: \( ... \)
        markdown = markdown.replace(/\\\(([\s\S]*?)\\\)/g, (match, formula) => {
            const escaped = formula.trim();
            return `<span class="latex-formula">$$${escaped}$$</span>`;
        });

        return markdown;
    }

    /**
     * Process bullet lists (- item)
     * Preserve indentation hierarchy using nested <ul> blocks.
     */
    private processBulletLists(html: string): string {
        const lines = html.split('\n');
        const output: string[] = [];
        const indentStack: number[] = [];
        const liOpenStack: boolean[] = [];

        const closeAllLists = () => {
            while (indentStack.length > 0) {
                const last = indentStack.length - 1;
                if (liOpenStack[last]) {
                    output.push('</li>');
                    liOpenStack[last] = false;
                }
                output.push('</ul>');
                indentStack.pop();
                liOpenStack.pop();
            }
        };

        for (const line of lines) {
            const match = line.match(/^(\s*)[\-\*]\s+(.*)$/);
            if (!match) {
                if (line.trim().length > 0) {
                    closeAllLists();
                    output.push(line);
                }
                continue;
            }

            const leadingWhitespace = (match[1] || '').replace(/\t/g, '    ');
            const indent = leadingWhitespace.length;
            let itemText = (match[2] || '').trim();
            itemText = itemText.replace(/\*\*([^\*]+)\*\*/g, '<strong>$1</strong>');
            itemText = itemText.replace(/\*([^\*]+)\*/g, '<em>$1</em>');

            if (indentStack.length === 0) {
                output.push('<ul>');
                indentStack.push(indent);
                liOpenStack.push(false);
            } else {
                const currentIndent = indentStack[indentStack.length - 1];

                if (indent > currentIndent) {
                    output.push('<ul>');
                    indentStack.push(indent);
                    liOpenStack.push(false);
                } else if (indent < currentIndent) {
                    while (indentStack.length > 0 && indent < indentStack[indentStack.length - 1]) {
                        const last = indentStack.length - 1;
                        if (liOpenStack[last]) {
                            output.push('</li>');
                            liOpenStack[last] = false;
                        }
                        output.push('</ul>');
                        indentStack.pop();
                        liOpenStack.pop();
                    }

                    if (indentStack.length === 0 || indentStack[indentStack.length - 1] !== indent) {
                        output.push('<ul>');
                        indentStack.push(indent);
                        liOpenStack.push(false);
                    }
                }
            }

            const currentLevel = liOpenStack.length - 1;
            if (currentLevel >= 0 && liOpenStack[currentLevel]) {
                output.push('</li>');
                liOpenStack[currentLevel] = false;
            }

            output.push(`<li>${itemText}`);
            liOpenStack[currentLevel] = true;
        }

        closeAllLists();
        return output.join('\n').trim();
    }

    /**
     * Process numbered lists (1. 2. 3.)
     * Fixed to handle multiple consecutive numbered items with proper formatting
     */
    private processNumberedLists(html: string): string {
        const lines = html.split('\n');
        let result = '';
        let inList = false;
        let lastWasEmpty = false;

        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            const isNumberedItem = /^\s*\d+\.\s+/.test(line);
            const isEmpty = line.trim().length === 0;

            if (isNumberedItem) {
                // Check if previous item was also numbered or we're starting a new list
                const prevLineIsNumbered = i > 0 && /^\s*\d+\.\s+/.test(lines[i - 1]);
                const prevLineWasListEnd = i > 0 && !(/^\s*\d+\.\s+/.test(lines[i - 1])) && inList;

                // Close previous list if needed
                if (inList && !prevLineIsNumbered && lastWasEmpty) {
                    result += '</ol>\n';
                    inList = false;
                }

                // Start new list if needed
                if (!inList) {
                    result += '<ol>\n';
                    inList = true;
                }

                // Extract number and text
                const match = line.match(/^\s*(\d+)\.\s+(.*)/);
                if (match) {
                    let itemText = match[2].trim();
                    
                    // Apply bold/italic formatting to list item text
                    itemText = itemText.replace(/\*\*([^\*]+)\*\*/g, '<strong>$1</strong>');
                    itemText = itemText.replace(/\*([^\*]+)\*/g, '<em>$1</em>');
                    
                    result += `<li>${itemText}</li>\n`;
                    lastWasEmpty = false;
                }
            } else {
                if (isEmpty) {
                    lastWasEmpty = true;
                    // Check if next line is numbered
                    const nextLineIsNumbered = i < lines.length - 1 && /^\s*\d+\.\s+/.test(lines[i + 1]);
                    if (inList && nextLineIsNumbered) {
                        // Continue list on next iteration
                        continue;
                    }
                } else {
                    lastWasEmpty = false;
                }

                // Close list if we hit non-numbered content
                if (inList && !isNumberedItem && line.trim().length > 0) {
                    result += '</ol>\n';
                    inList = false;
                    result += line + '\n';
                } else if (!isNumberedItem && line.trim().length > 0) {
                    result += line + '\n';
                }
            }
        }

        if (inList) {
            result += '</ol>\n';
        }

        return result.trim();
    }

    /**
     * Process paragraphs and line breaks
     */
    private processParagraphs(html: string): string {
        const blockTags = '(?:h[1-6]|p|ul|ol|li|div|pre|hr|table|thead|tbody|tr|th|td|details|summary)';
        const startsWithBlockTag = new RegExp(`^<\\/?${blockTags}\\b`, 'i');
        const paragraphs = html
            .replace(new RegExp(`\\n(?=<\\/?${blockTags}\\b)`, 'gi'), '\n\n')
            .replace(new RegExp(`(<\\/${blockTags}>|<hr\\b[^>]*>)\\n`, 'gi'), '$1\n\n')
            .split(/\n{2,}/).filter(p => p.trim().length > 0);

        return paragraphs
            .map(para => {
                // Skip if already wrapped in tags
                if (startsWithBlockTag.test(para)) {
                    return para;
                }
                const paragraphWithBreaks = para.replace(/\n/g, '<br>');
                return `<p>${paragraphWithBreaks}</p>`;
            })
            .join('\n');
    }

    private htmlToText(html: string): string {
        return (new DOMParser().parseFromString(html, 'text/html').body.textContent || '').trim();
    }

    /**
     * Escape HTML special characters to prevent injection
     */
    private escapeHtml(text: string): string {
        const map: { [key: string]: string } = {
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            '"': '&quot;',
            "'": '&#039;'
        };
        return text.replace(/[&<>"']/g, char => map[char]);
    }

    /**
     * Cache HTML result with LRU (Least Recently Used) eviction
     */
    private cacheHtml(markdown: string, html: SafeHtml): void {
        // Simple LRU: if cache is full, remove oldest entry
        if (this.cache.size >= this.config.maxCacheSize) {
            const firstKey = this.cache.keys().next().value as string;
            if (firstKey) {
                this.cache.delete(firstKey);
            }
        }
        this.cache.set(markdown, html);
    }
}
